import {
  getPublicErrorPayload,
  type PublicErrorPayload,
} from '../../utils/apollo/apolloErrorMessage'

// Cap free-form details text so a long backend string can't bloat the RUM error
// title (and, transitively, the default grouping).
const MAX_DETAILS_TEXT = 200

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

/**
 * Splits the sanctioned `public_error_details` channel into the two pieces we
 * report differently:
 *
 *   - `code`: a low-cardinality, enum-like identifier. Many backends nest the
 *     fine-grained error code at `public_error_details.code`. Safe for both the
 *     RUM message title AND the Datadog fingerprint.
 *   - `text`: a bare string payload (legacy shape). Informative for the message,
 *     but kept OUT of the fingerprint since free-form text is high-cardinality
 *     and would fragment grouping.
 */
const extractDetailsParts = (details: unknown): { code?: string; text?: string } => {
  if (typeof details === 'string') {
    const trimmed = details.trim()
    return trimmed ? { text: trimmed.slice(0, MAX_DETAILS_TEXT) } : {}
  }
  if (isRecord(details) && typeof details.code === 'string' && details.code) {
    return { code: details.code }
  }
  return {}
}

/**
 * Resolves `extensions` from either a bare GraphQL error or an ApolloError-shaped
 * object (`graphQLErrors[0]`), mirroring `getPublicErrorPayload`'s unwrap so the
 * legacy fallback below reads the same error `getPublicErrorPayload` did.
 */
const getExtensions = (graphQLError: unknown): Record<string, unknown> | undefined => {
  if (!isRecord(graphQLError)) return undefined
  const gqlErrors = graphQLError.graphQLErrors
  const first =
    Array.isArray(gqlErrors) && gqlErrors.length > 0 && isRecord(gqlErrors[0])
      ? gqlErrors[0]
      : graphQLError
  return isRecord(first.extensions) ? first.extensions : undefined
}

/**
 * Normalizes `extensions.details` into the hash `extractDetailsParts` expects.
 *
 * Some backends don't populate the sanctioned `output.meta` channel and instead
 * put the structured error payload on `extensions.details` as a JSON string —
 * sometimes double-wrapped as `{"public_error_details":"<inner-json>"}`. Parse it
 * back to an object so the fallback can surface the same `code` discriminator (and
 * any free-form text) the sanctioned path does. Non-string or unparseable values
 * are returned untouched.
 */
const parseExtensionsDetails = (details: unknown): unknown => {
  if (typeof details !== 'string') return details
  try {
    const outer: unknown = JSON.parse(details)
    if (isRecord(outer) && typeof outer.public_error_details === 'string') {
      try {
        return JSON.parse(outer.public_error_details)
      } catch {
        return outer.public_error_details
      }
    }
    return outer
  } catch {
    return details
  }
}

export interface GraphQLErrorReportDetails {
  /**
   * The sanctioned client-safe payload (`public_error_*`) for attaching to the
   * RUM error context, or `undefined` when the backend didn't populate it.
   */
  publicError: PublicErrorPayload | undefined
  /**
   * Space-joined discriminators to append to the human-readable RUM message,
   * e.g. ` [DRIVER_INACTIVE]`. Empty string when there's nothing to add.
   */
  messageSuffix: string
  /**
   * Underscore-joined discriminators to append to the Datadog `dd_fingerprint`,
   * e.g. `_DRIVER_INACTIVE`. Empty string when there's nothing to add, so
   * errors without a public payload keep their existing fingerprint bucket.
   */
  fingerprintSuffix: string
}

/**
 * Derives extra reporting signal from the mesh's sanctioned public-error channel
 * (`public_error_code` / `error_types` / `public_error_details`).
 *
 * The mesh masks internal error messages to a generic `"An error has occurred."`,
 * so a single (`extensions.code`, `operationName`) pair rolls up many distinct
 * backend failures in RUM/Datadog. This surfaces the distinguishing fields into
 * the reported message, the fingerprint, and the error context so on-call can
 * tell those failures apart. See `getPublicErrorPayload` and the IPP Error
 * Handling Strategy for the channel contract.
 *
 * When the sanctioned channel is absent, falls back to `extensions.details`, the
 * JSON string frigate services emit (see `parseExtensionsDetails`), so those
 * errors also get a discriminator.
 *
 * `extensionsCode` is the top-level `extensions.code` the message already carries
 * (passed so we can drop a redundant `public_error_code` that merely echoes it).
 */
export const getGraphQLErrorReportDetails = (
  graphQLError: unknown,
  extensionsCode?: unknown
): GraphQLErrorReportDetails => {
  const publicError = getPublicErrorPayload(graphQLError)

  const codeStr = extensionsCode == null ? undefined : String(extensionsCode)
  const publicCode = publicError?.public_error_code
  let { code: detailsCode, text: detailsText } = extractDetailsParts(
    publicError?.public_error_details
  )

  // Fall back to `extensions.details` ONLY when the sanctioned channel is entirely
  // absent — frigate services that use this field don't populate `output.meta`, so
  // a present `publicError` means a migrated backend we should trust wholesale.
  // Without this fallback, those errors report the generic masked message with no
  // discriminator.
  if (!publicError) {
    const extensionsDetails = getExtensions(graphQLError)?.details
    if (extensionsDetails != null) {
      ;({ code: detailsCode, text: detailsText } = extractDetailsParts(
        parseExtensionsDetails(extensionsDetails)
      ))
    }
  }

  // Enum-like discriminators, safe for both the message and the fingerprint.
  const fingerprintTokens = [
    // Include the public code only when it adds signal beyond extensions.code,
    // which the message/fingerprint already carry.
    publicCode && publicCode !== codeStr ? publicCode : undefined,
    ...(publicError?.error_types ?? []),
    detailsCode,
  ].filter((token): token is string => Boolean(token))

  // The message additionally carries free-form details text (kept out of the
  // fingerprint to avoid fragmenting grouping).
  const messageTokens = [...fingerprintTokens, detailsText].filter((token): token is string =>
    Boolean(token)
  )

  return {
    publicError,
    messageSuffix: messageTokens.length ? ` [${messageTokens.join(' ')}]` : '',
    fingerprintSuffix: fingerprintTokens.length
      ? `_${fingerprintTokens.map(token => token.replace(/\s+/g, '-')).join('_')}`
      : '',
  }
}
